import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { MoreHorizontal } from 'lucide-react';
import { IconButton } from './Button.tsx';

export interface MenuItem {
  label: string;
  icon?: ReactNode;
  onSelect(): void;
  danger?: boolean;
  hidden?: boolean;
}

/** Overflow menu with keyboard support (arrows, Home/End, Escape). */
export function Menu({ items, label = 'More actions', trigger }: { items: MenuItem[]; label?: string; trigger?: ReactNode }) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const id = useId();
  const visible = items.filter((i) => !i.hidden);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!root.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    root.current?.querySelector<HTMLButtonElement>('[role="menuitem"]')?.focus();
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  if (visible.length === 0) return null;

  const onKeyDown = (e: React.KeyboardEvent) => {
    const nodes = [...(root.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]') ?? [])];
    const idx = nodes.indexOf(document.activeElement as HTMLButtonElement);
    if (e.key === 'Escape') {
      setOpen(false);
      button.current?.focus();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      nodes[(idx + 1) % nodes.length]?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      nodes[(idx - 1 + nodes.length) % nodes.length]?.focus();
    } else if (e.key === 'Home') nodes[0]?.focus();
    else if (e.key === 'End') nodes[nodes.length - 1]?.focus();
    else if (e.key === 'Tab') setOpen(false);
  };

  return (
    <div className="ui-menu" ref={root} onKeyDown={onKeyDown}>
      <IconButton ref={button} label={label} aria-haspopup="menu" aria-expanded={open} aria-controls={open ? id : undefined} onClick={() => setOpen((o) => !o)}>
        {trigger ?? <MoreHorizontal aria-hidden="true" />}
      </IconButton>
      {open && (
        <div className="ui-menu__list" role="menu" id={id} aria-label={label}>
          {visible.map((item) => (
            <button
              key={item.label}
              type="button"
              role="menuitem"
              className={`ui-menu__item${item.danger ? ' ui-menu__item--danger' : ''}`}
              onClick={() => {
                setOpen(false);
                item.onSelect();
              }}
            >
              {item.icon}
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
